/**
 * useQuizTimer - Client-side Timer with Server Sync
 * 
 * Handles the countdown timer for quiz questions:
 * - Calculates remaining time based on server timestamp
 * - Accounts for server time offset (clock skew)
 * - Supports bonus time additions
 * - Supports pause/resume functionality
 * - Triggers expiration state
 * 
 * Timer State Format (from Firebase):
 * {
 *   questionStartedAt: number,   // When timer was last started/resumed
 *   timerDurationMs: number,     // Base timer duration
 *   bonusTimeAddedMs: number,    // Additional time added
 *   remainingMsWhenPaused: number | null,  // Remaining time when paused
 *   isPaused: boolean,           // Whether timer is currently paused
 *   questionIndex: number        // Current question index
 * }
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Timer urgency levels for styling
 */
export const TIMER_URGENCY = {
  NORMAL: 'normal',
  WARNING: 'warning',
  URGENT: 'urgent',
  CRITICAL: 'critical',
};

/**
 * Custom hook for quiz timer management
 * @param {Object} timerState - Timer state from Firebase
 * @param {number} serverTimeOffset - Offset between client and server time
 * @param {boolean} submitted - Whether the answer has been submitted
 * @returns {Object} Timer state and values
 */
export function useQuizTimer(timerState, serverTimeOffset, submitted = false) {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timerExpired, setTimerExpired] = useState(false);
  const intervalRef = useRef(null);

  // Calculate and update remaining time
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // No timer if no timer state
    if (!timerState || !timerState.timerDurationMs) {
      setTimeRemaining(null);
      setTimerExpired(false);
      return;
    }

    // Handle paused state - show the frozen remaining time
    if (timerState.isPaused && timerState.remainingMsWhenPaused !== undefined) {
      const pausedSeconds = Math.max(0, Math.ceil(timerState.remainingMsWhenPaused / 1000));
      setTimeRemaining(pausedSeconds);
      setTimerExpired(pausedSeconds <= 0);
      return; // Don't start interval when paused
    }

    // Timer is not started yet (no questionStartedAt)
    if (!timerState.questionStartedAt) {
      // Show initial duration if timer hasn't started
      const initialSeconds = Math.ceil((timerState.timerDurationMs + (timerState.bonusTimeAddedMs || 0)) / 1000);
      setTimeRemaining(initialSeconds);
      setTimerExpired(false);
      return; // Don't start interval - waiting for admin to start
    }

    const calculateRemainingTime = () => {
      const {
        questionStartedAt,
        timerDurationMs,
        bonusTimeAddedMs = 0,
        remainingMsWhenPaused
      } = timerState;

      // Calculate server time accounting for offset
      const serverTime = Date.now() + serverTimeOffset;
      
      // If there's a remainingMsWhenPaused, use that as the basis for calculation
      // This happens when timer was paused and then resumed
      let effectiveDuration;
      if (remainingMsWhenPaused !== undefined && remainingMsWhenPaused !== null && !timerState.isPaused) {
        // Timer was resumed - use the paused remaining time plus any bonus
        effectiveDuration = remainingMsWhenPaused + bonusTimeAddedMs;
      } else {
        // Timer started fresh - use full duration plus bonus
        effectiveDuration = timerDurationMs + bonusTimeAddedMs;
      }
      
      // Calculate elapsed time since question started (or resumed)
      const elapsed = serverTime - questionStartedAt;
      
      // Calculate remaining time in seconds
      const remainingMs = effectiveDuration - elapsed;
      const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));

      return remainingSeconds;
    };

    // Initial calculation
    const initialRemaining = calculateRemainingTime();
    setTimeRemaining(initialRemaining);
    
    // Update timerExpired based on current calculation
    // This allows the timer to "resurrect" if bonus time is added
    const isExpired = initialRemaining <= 0;
    setTimerExpired(isExpired);

    // If already expired or submitted, don't start interval
    if (isExpired || submitted) {
      return;
    }

    // Update timer every second
    intervalRef.current = setInterval(() => {
      const remaining = calculateRemainingTime();
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        setTimerExpired(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }, 1000);

    // Cleanup on unmount or dependency change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timerState, serverTimeOffset, submitted]);

  /**
   * Check if timer is currently active
   * Timer is active when there's time remaining and it hasn't expired
   */
  const timerActive = timeRemaining !== null && timeRemaining > 0;

  /**
   * Get max timer duration in seconds (for progress bar calculation)
   */
  const maxTimerSeconds = timerState?.timerDurationMs 
    ? Math.ceil((timerState.timerDurationMs + (timerState.bonusTimeAddedMs || 0)) / 1000)
    : 30;

  /**
   * Get timer progress percentage (0-100)
   */
  const timerProgress = timeRemaining !== null 
    ? (timeRemaining / maxTimerSeconds) * 100 
    : 100;

  /**
   * Get timer urgency level for styling
   */
  const getUrgencyLevel = useCallback(() => {
    if (timeRemaining === null) return TIMER_URGENCY.NORMAL;
    if (timeRemaining <= 5) return TIMER_URGENCY.CRITICAL;
    if (timeRemaining <= 10) return TIMER_URGENCY.WARNING;
    return TIMER_URGENCY.NORMAL;
  }, [timeRemaining]);

  /**
   * Get color class based on urgency
   */
  const getTimerColorClass = useCallback(() => {
    const urgency = getUrgencyLevel();
    switch (urgency) {
      case TIMER_URGENCY.CRITICAL:
        return 'bg-red-500';
      case TIMER_URGENCY.WARNING:
        return 'bg-orange-500';
      default:
        return 'bg-green-500';
    }
  }, [getUrgencyLevel]);

  /**
   * Get text color class based on urgency
   */
  const getTimerTextColorClass = useCallback(() => {
    const urgency = getUrgencyLevel();
    switch (urgency) {
      case TIMER_URGENCY.CRITICAL:
        return 'text-red-600 dark:text-red-400';
      case TIMER_URGENCY.WARNING:
        return 'text-orange-600 dark:text-orange-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  }, [getUrgencyLevel]);

  return {
    // Timer values
    timeRemaining,
    timerExpired,
    timerActive,
    maxTimerSeconds,
    timerProgress,
    isPaused: timerState?.isPaused || false,

    // Utility functions
    getUrgencyLevel,
    getTimerColorClass,
    getTimerTextColorClass,

    // Raw state for debugging
    hasTimer: timerState !== null && timerState.timerDurationMs !== undefined,
  };
}

export default useQuizTimer;
